import React, { useState } from 'react';
import { ArrowRightLeft, Gem, Loader2, ShieldCheck } from 'lucide-react';

import { gbApi, gbFmt, gbError } from './gbApi';
import { gbAviso } from './GbNotice';
import { GbCoin } from './GbCoin';
import { gbSfx } from './gbSfx';

const MOTIVO = {
  insufficient_diamonds: 'Saldo de diamantes insuficiente — o valor foi conferido no servidor.',
  invalid_amount: 'Informe uma quantidade inteira de diamantes.',
  exchange_disabled: 'O intercâmbio está desligado neste momento.',
};

/* Intercâmbio: diamantes → moedas amarelas. O servidor é quem confere o saldo e aplica a taxa;
   aqui só mostramos a prévia e pedimos confirmação antes de enviar. */
export const GbExchange = ({ saldo, taxa, maxOp, onChange }) => {
  const [qtd, setQtd] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const diamantes = /^\d+$/.test(qtd) ? Number(qtd) : 0;
  const teto = Math.min(Number(saldo || 0), Number(maxOp || 0) || Infinity);
  const valido = diamantes >= 1 && diamantes <= teto;
  const moedas = diamantes * Number(taxa || 0);

  if (!taxa) return null;

  const trocar = async () => {
    if (!valido || enviando) return;
    setEnviando(true);
    try {
      const { data } = await gbApi.post('/diamonds/exchange', { diamonds: diamantes });
      gbSfx('coin');
      gbAviso.ok(`Intercâmbio feito: ${gbFmt(data.diamonds)} diamantes viraram ${gbFmt(data.coins)} moedas amarelas.`);
      setQtd(''); setConfirmando(false);
      await onChange?.();
    } catch (e) {
      const motivo = e?.response?.data?.detail;
      gbAviso.erro(MOTIVO[motivo] || gbError(e));
      setConfirmando(false);
      await onChange?.();
    } finally { setEnviando(false); }
  };

  return (
    <div className="mt-3 rounded-3xl border border-cyan-700/50 bg-gradient-to-br from-cyan-950/30 via-[#0e071a] to-black/60 p-4"
      data-testid="gb-exchange">
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-cyan-300">
        <ArrowRightLeft className="h-4 w-4" />intercâmbio
      </p>
      <p className="mt-1 text-[11px] leading-snug text-cyan-100/75" data-testid="gb-exchange-rate">
        Troque diamantes por moedas amarelas para duelar no PvP:{' '}
        <b className="text-cyan-200">1 diamante = {gbFmt(taxa)} moedas</b>. A troca é definitiva.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="flex min-w-[150px] flex-1 items-center gap-2 rounded-2xl border border-cyan-800/60 bg-black/50 px-3 py-2">
          <Gem className="h-4 w-4 flex-none text-cyan-300" />
          <input value={qtd} inputMode="numeric" pattern="[0-9]*" placeholder="quantos diamantes"
            onChange={(e) => { setConfirmando(false); setQtd(e.target.value.replace(/\D/g, '').slice(0, 7)); }}
            data-testid="gb-exchange-input"
            className="w-full bg-transparent font-mono text-lg font-black text-cyan-100 outline-none placeholder:text-cyan-700/70" />
        </label>
        <button onClick={() => { setConfirmando(false); setQtd(String(Math.max(0, Math.floor(teto)))); }}
          data-testid="gb-exchange-all"
          className="rounded-full border border-cyan-700/60 px-3 py-2 text-[11px] font-black text-cyan-200 hover:border-cyan-400">
          tudo
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-amber-800/40 bg-amber-950/20 px-3 py-2"
        data-testid="gb-exchange-preview">
        <span className="text-[11px] font-bold text-purple-200/70">você recebe</span>
        <span className="flex items-center gap-1.5 font-mono text-xl font-black text-amber-200">
          <GbCoin className="h-[0.9em]" />{gbFmt(moedas)}
        </span>
      </div>

      {diamantes > teto && (
        <p className="mt-2 text-[11px] font-bold text-rose-300" data-testid="gb-exchange-over">
          Você tem {gbFmt(saldo)} diamantes — não dá para trocar mais do que isso.
        </p>
      )}

      {!confirmando ? (
        <button onClick={() => { gbSfx('click'); setConfirmando(true); }} disabled={!valido}
          data-testid="gb-exchange-btn"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-cyan-300 py-3 text-xs font-black uppercase tracking-wider text-[#04141a] shadow-[0_0_24px_rgba(34,211,238,.35)] transition-transform hover:scale-[1.01] active:scale-[.98] disabled:opacity-40 disabled:hover:scale-100">
          <ArrowRightLeft className="h-4 w-4" />trocar diamantes por moedas
        </button>
      ) : (
        <div className="mt-3 rounded-2xl border border-amber-500/60 bg-amber-950/40 p-3" data-testid="gb-exchange-confirm">
          <p className="text-sm font-bold text-amber-100">
            Confirmar a troca de <b>{gbFmt(diamantes)} diamantes</b> por <b>{gbFmt(moedas)} moedas amarelas</b>?
          </p>
          <div className="mt-2 flex gap-2">
            <button onClick={trocar} disabled={enviando} data-testid="gb-exchange-confirm-yes"
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-2.5 text-xs font-black uppercase text-[#1b0d02] disabled:opacity-60">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              confirmar
            </button>
            <button onClick={() => setConfirmando(false)} disabled={enviando} data-testid="gb-exchange-confirm-no"
              className="rounded-xl border border-purple-700/60 px-4 py-2.5 text-xs font-black uppercase text-purple-200">
              voltar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
